import {
  PublicSanitizedImageContentType,
  type WebsiteLogoContentType,
} from 'src/api/generated';
import type {
  WebsiteLogoUploadErrorCode,
  WebsiteLogoValidationErrorCode,
} from '../types/website-logo-upload.types';

/** Maximum accepted website-logo size in bytes. */
export const WEBSITE_LOGO_MAX_SIZE_BYTES = 5 * 1024 * 1024;
/** Browser file-picker filter for every supported website-logo format. */
export const WEBSITE_LOGO_ACCEPT =
  '.png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml';
/** Maximum time allowed for the presigned logo PUT request. */
export const WEBSITE_LOGO_UPLOAD_TIMEOUT_MS = 120_000;

/** Error raised when a local logo fails deterministic client validation. */
export class WebsiteLogoValidationError extends Error {
  /**
   * Creates a validation error with a stable translation-friendly code.
   *
   * @param code - Validation condition that rejected the file.
   */
  constructor(public readonly code: WebsiteLogoValidationErrorCode) {
    super(code);
    this.name = 'WebsiteLogoValidationError';
  }
}

/** Error raised when the presigned logo PUT does not complete successfully. */
export class WebsiteLogoUploadError extends Error {
  /**
   * Creates a transport error without exposing hard-coded user-facing copy.
   *
   * @param code - Stable transport failure category.
   * @param status - Optional HTTP response status for diagnostics.
   */
  constructor(
    public readonly code: WebsiteLogoUploadErrorCode,
    public readonly status?: number
  ) {
    super(code);
    this.name = 'WebsiteLogoUploadError';
  }
}

const CONTENT_TYPE_EXTENSIONS: Record<WebsiteLogoContentType, string[]> = {
  [PublicSanitizedImageContentType.IMAGE_PNG]: ['png'],
  [PublicSanitizedImageContentType.IMAGE_JPEG]: ['jpg', 'jpeg'],
  [PublicSanitizedImageContentType.IMAGE_WEBP]: ['webp'],
  [PublicSanitizedImageContentType.IMAGE_SVG_XML]: ['svg'],
};

const WEBSITE_LOGO_CONTENT_TYPE_BY_MIME: Partial<
  Record<string, WebsiteLogoContentType>
> = {
  'image/png': PublicSanitizedImageContentType.IMAGE_PNG,
  'image/jpeg': PublicSanitizedImageContentType.IMAGE_JPEG,
  'image/webp': PublicSanitizedImageContentType.IMAGE_WEBP,
  'image/svg+xml': PublicSanitizedImageContentType.IMAGE_SVG_XML,
};

const getExtension = (fileName: string): string =>
  fileName.split('.').at(-1)?.toLowerCase() ?? '';

const bytesToHex = (bytes: Uint8Array): string =>
  Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return btoa(binary);
};

/**
 * Validates a selected logo's size, MIME type, and filename extension.
 *
 * @param file - Browser-selected logo candidate.
 * @returns The canonical content type sent to the website API.
 * @throws {WebsiteLogoValidationError} When the file is empty, oversized, or
 * has an unsupported or mismatched MIME type and extension.
 */
export const validateWebsiteLogoFile = (file: File): WebsiteLogoContentType => {
  if (file.size <= 0) {
    throw new WebsiteLogoValidationError('empty');
  }
  if (file.size > WEBSITE_LOGO_MAX_SIZE_BYTES) {
    throw new WebsiteLogoValidationError('too-large');
  }
  const contentType = WEBSITE_LOGO_CONTENT_TYPE_BY_MIME[file.type];
  if (!contentType) {
    throw new WebsiteLogoValidationError('unsupported-type');
  }
  if (!CONTENT_TYPE_EXTENSIONS[contentType].includes(getExtension(file.name))) {
    throw new WebsiteLogoValidationError('extension-mismatch');
  }

  return contentType;
};

/**
 * Calculates the SHA-256 checksum representations required by S3 and Files.
 *
 * @param file - Logo bytes to digest.
 * @returns Hexadecimal and base64 SHA-256 representations.
 */
export const calculateWebsiteLogoChecksum = async (
  file: File
): Promise<{ hex: string; base64: string }> => {
  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    await file.arrayBuffer()
  );
  const bytes = new Uint8Array(digest);
  return {
    hex: bytesToHex(bytes),
    base64: bytesToBase64(bytes),
  };
};

/**
 * Uploads logo bytes to a presigned URL while reporting browser progress.
 *
 * @param uploadUrl - Presigned Files upload URL.
 * @param uploadHeaders - Headers signed into the upload session.
 * @param file - Validated logo bytes.
 * @param onProgress - Called with upload completion from zero to one hundred.
 * @param signal - Optional cancellation signal, aborted when the view unmounts.
 * @returns A promise that resolves after a successful HTTP response.
 * @throws {WebsiteLogoUploadError} When the request fails, times out, is
 * cancelled, or returns a non-success status.
 */
export const uploadWebsiteLogoBytes = (
  uploadUrl: string,
  uploadHeaders: Record<string, string>,
  file: File,
  onProgress: (progress: number) => void,
  signal?: AbortSignal
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new WebsiteLogoUploadError('aborted'));
      return;
    }

    const request = new XMLHttpRequest();
    let settled = false;
    const settle = (callback: () => void): void => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', abortRequest);
      callback();
    };
    const resolveUpload = (): void => settle(resolve);
    const rejectUpload = (
      code: WebsiteLogoUploadErrorCode,
      status?: number
    ): void => settle(() => reject(new WebsiteLogoUploadError(code, status)));
    const abortRequest = (): void => {
      request.abort();
      rejectUpload('aborted');
    };

    request.open('PUT', uploadUrl);
    request.timeout = WEBSITE_LOGO_UPLOAD_TIMEOUT_MS;
    Object.entries(uploadHeaders).forEach(([name, value]) => {
      request.setRequestHeader(name, value);
    });
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(100);
        resolveUpload();
        return;
      }

      rejectUpload('http', request.status);
    });
    request.addEventListener('error', () => {
      rejectUpload('network');
    });
    request.addEventListener('timeout', () => {
      rejectUpload('timeout');
    });
    request.addEventListener('abort', () => {
      rejectUpload('aborted');
    });
    signal?.addEventListener('abort', abortRequest, { once: true });
    request.send(file);
  });
